import React, { useState } from 'react';
import { X, MinusCircle, PlusCircle } from 'lucide-react';
import { StockLogo } from '../../utils/stockLogo';
import { useCurrency } from '../../contexts/CurrencyContext';

const AssetQuantityModal = ({ asset, onClose, onUpdate }) => {
  const { formatAmount } = useCurrency();
  const [quantity, setQuantity] = useState(asset.quantity || 0);
  const [avgCost, setAvgCost] = useState(asset.avgCost || 0);
  const [isLoading, setIsLoading] = useState(false);

  const isCurrency = asset.symbol?.endsWith('CURRENCY');
  const assetCurrency = isCurrency ? asset.symbol.split('.')[0] : (asset.currency || 'USD');
  const increment = isCurrency ? 1 : 1;

  const handleQuantityChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setQuantity(value);
    }
  };

  const handleAvgCostChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      setAvgCost(value);
    }
  };

  const handleIncrement = () => {
    setQuantity(prev => prev + increment);
  };

  const handleDecrement = () => {
    setQuantity(prev => Math.max(0, prev - increment));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const updateData = {
        quantity,
        currency: assetCurrency,
        exchange: asset.exchange || (isCurrency ? 'IDEALPRO' : 'ARCA'),
        secType: isCurrency ? 'CASH' : 'STK',
        symbol: asset.symbol
      };

      // Currency için özel değerler
      if (isCurrency) {
        updateData.avgCost = 1;
        updateData.value = quantity;
      } else {
        updateData.avgCost = avgCost;
        updateData.value = quantity * avgCost;
      }

      await onUpdate(asset.symbol, updateData);
      onClose();
    } catch (error) {
      console.error('Update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayValue = () => {
    if (isCurrency) {
      return formatAmount(quantity, assetCurrency);
    }
    return `${quantity} adet`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            {isCurrency ? (
              <img 
                src={`/images/currencys/${assetCurrency.toLowerCase()}.png`}
                alt={assetCurrency}
                className="h-8 w-8"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<span className='text-lg font-medium'>${assetCurrency}</span>`;
                }}
              />
            ) : (
              <StockLogo symbol={asset.symbol} symbolInfo={asset} size="lg" />
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {asset.description || asset.symbol}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isCurrency ? assetCurrency : asset.symbol}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {isCurrency ? 'Para Miktarı' : 'Varlık Miktarı'}
              </label>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={handleDecrement}
                  className="text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <MinusCircle className="w-6 h-6" />
                </button>
                
                <div className="flex-1">
                  <input
                    type="number"
                    value={quantity}
                    onChange={handleQuantityChange}
                    min="0"
                    step={increment}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {getDisplayValue()}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleIncrement}
                  className="text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <PlusCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {!isCurrency && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ortalama Maliyet
                </label>
                <input
                  type="number"
                  value={avgCost}
                  onChange={handleAvgCostChange}
                  min="0"
                  step="0.01"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Güncelleniyor...' : 'Güncelle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetQuantityModal;